/**
 * CoreMetricsGrid Component
 * 
 * Responsive 4-column grid displaying core market metrics with AI glass cards,
 * trend indicators, loading skeletons, and mobile-responsive design
 */

import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDollarSign, 
  faChartArea, 
  faClock, 
  faHome,
  faRulerCombined,
  faHandshake,
  faArrowUp,
  faArrowDown,
  faMinus
} from '@fortawesome/free-solid-svg-icons';

// Import types
import type { CoreMetricsGridProps } from '../../../../types/marketIntelligence.types';

// Metric configuration
interface MetricConfig {
  key: string;
  label: string;
  icon: any;
  color: string;
  format: 'currency' | 'number' | 'decimal' | 'percentage';
  description: string;
}

const METRICS_CONFIG: MetricConfig[] = [
  {
    key: 'medianPrice',
    label: 'Median Price',
    icon: faDollarSign,
    color: '#10b981',
    format: 'currency',
    description: 'Median closing price for properties'
  },
  {
    key: 'totalVolume',
    label: 'Total Volume',
    icon: faChartArea,
    color: '#3b82f6',
    format: 'currency',
    description: 'Total sales volume in period'
  },
  {
    key: 'medianDOM',
    label: 'Median DOM',
    icon: faClock,
    color: '#f59e0b',
    format: 'number',
    description: 'Median days on market'
  },
  {
    key: 'activeListings',
    label: 'Active Listings',
    icon: faHome,
    color: '#8b5cf6',
    format: 'number',
    description: 'Currently active listings'
  },
  {
    key: 'pricePerSqft',
    label: 'Price/Sqft',
    icon: faRulerCombined,
    color: '#ef4444',
    format: 'currency',
    description: 'Median price per square foot'
  },
  {
    key: 'salesCount',
    label: 'Sales Count',
    icon: faHandshake,
    color: '#06b6d4',
    format: 'number',
    description: 'Number of sales in period'
  }
];

// Format value based on type
const formatValue = (value: number | undefined, format: string): string => {
  if (value === undefined || value === null) return 'N/A';
  
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    
    case 'percentage':
      return `${value.toFixed(1)}%`;
    
    case 'decimal':
      return value.toFixed(1);
    
    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(value);
  }
};

// Get trend indicator (mock for now - would be calculated from historical data)
const getTrendIndicator = (value: number | undefined, metricKey: string) => {
  if (!value) return null;
  
  // Mock trend calculation (in real implementation, this would compare with historical data)
  const trendValue = Math.random() * 20 - 10; // Random -10 to +10
  const isPositive = trendValue > 2;
  const isNegative = trendValue < -2;
  const isNeutral = !isPositive && !isNegative;
  
  return {
    direction: isPositive ? 'up' : isNegative ? 'down' : 'neutral',
    value: Math.abs(trendValue),
    isGood: (isPositive && ['medianPrice', 'totalVolume', 'salesCount', 'pricePerSqft'].includes(metricKey)) ||
            (isNegative && ['medianDOM'].includes(metricKey))
  };
};

// Loading skeleton component
const MetricSkeleton: React.FC = () => (
  <Card 
    className="h-100 border-0"
    style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
      backdropFilter: 'blur(15px)',
      border: '1px solid rgba(0, 212, 255, 0.2)',
      borderRadius: '16px'
    }}
  >
    <Card.Body className="p-3">
      {/* Icon skeleton */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div 
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite'
          }}
        />
        <div 
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite'
          }}
        />
      </div>
      
      {/* Value skeleton */}
      <div 
        className="mb-2"
        style={{
          width: '80%',
          height: '28px',
          borderRadius: '6px',
          background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s infinite'
        }}
      />
      
      {/* Label skeleton */}
      <div 
        style={{
          width: '60%',
          height: '16px',
          borderRadius: '4px',
          background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s infinite'
        }}
      />
    </Card.Body>
  </Card>
);

// Individual metric card component
interface MetricCardProps {
  config: MetricConfig;
  value: number | undefined;
  loading: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ config, value, loading }) => {
  const trend = getTrendIndicator(value, config.key);
  
  if (loading) {
    return <MetricSkeleton />;
  }

  return (
    <Card 
      className="h-100 border-0 metric-card"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '16px',
        transition: 'all 0.3s ease'
      }}
    >
      <Card.Body className="p-3">
        {/* Header with icon and trend */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div 
            className="d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${config.color}20 0%, ${config.color}10 100%)`,
              border: `1px solid ${config.color}30`
            }}
          >
            <FontAwesomeIcon 
              icon={config.icon}
              style={{ 
                color: config.color,
                fontSize: '18px'
              }}
            />
          </div>
          
          {/* Trend indicator */}
          {trend && (
            <div 
              className="d-flex align-items-center"
              style={{
                padding: '4px 8px',
                borderRadius: '20px',
                background: trend.isGood 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                border: `1px solid ${trend.isGood ? '#10b98130' : '#ef444430'}`
              }}
            >
              <FontAwesomeIcon
                icon={trend.direction === 'up' ? faArrowUp : 
                      trend.direction === 'down' ? faArrowDown : faMinus}
                style={{
                  fontSize: '10px',
                  color: trend.isGood ? '#10b981' : '#ef4444',
                  marginRight: '4px'
                }}
              />
              <span 
                style={{ 
                  fontSize: '10px',
                  fontWeight: '600',
                  color: trend.isGood ? '#10b981' : '#ef4444'
                }}
              >
                {trend.value.toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        {/* Value */}
        <div 
          className="mb-2"
          style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1f2937',
            lineHeight: 1.2
          }}
        >
          {formatValue(value, config.format)}
        </div>

        {/* Label */}
        <div 
          className="text-muted"
          style={{ 
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          {config.label}
        </div>

        {/* Description on hover */}
        <div 
          className="metric-description mt-2"
          style={{
            fontSize: '0.75rem',
            color: '#6b7280',
            opacity: 0,
            maxHeight: 0,
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
        >
          {config.description}
        </div>
      </Card.Body>
    </Card>
  );
};

const CoreMetricsGrid: React.FC<CoreMetricsGridProps> = ({
  medianPrice,
  totalVolume,
  medianDOM,
  activeListing,
  pricePerSqft,
  salesCount,
  loading = false
}) => {
  // Map props to values
  const values = {
    medianPrice,
    totalVolume,
    medianDOM,
    activeListings: activeListing,
    pricePerSqft,
    salesCount
  };

  return (
    <div className="core-metrics-grid">
      <Row className="g-3">
        {METRICS_CONFIG.map((config, index) => (
          <Col 
            key={config.key}
            xs={12} 
            sm={6} 
            lg={4} 
            xl={2}
            className="d-flex metric-card"
          >
            <MetricCard
              config={config}
              value={values[config.key as keyof typeof values]}
              loading={loading}
            />
          </Col>
        ))}
      </Row>

      {/* Custom CSS */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        .metric-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
        }
        
        .metric-card:hover .metric-description {
          opacity: 1;
          max-height: 50px;
        }
        
        @media (max-width: 500px) {
          /* Remove d-flex from nested divs */
          .core-metrics-grid div .metric-card {
            display: block !important;
          }
          
          /* Override the display: block !important that's breaking flex layout */
          .core-metrics-grid .metric-card .card-body .d-flex.align-items-center.justify-content-between.mb-3 {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            width: 100% !important;
          }
          
          .core-metrics-grid .metric-card .card-body .d-flex.align-items-center.justify-content-between {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            width: 100% !important;
          }
          
          .core-metrics-grid .metric-card .card-body .d-flex.align-items-center.justify-content-between > div:last-child {
            margin-left: auto !important;
            margin-right: 0 !important;
          }
          
          /* Target the trend indicator specifically */
          .core-metrics-grid .metric-card .d-flex.align-items-center[style*="padding: 4px 8px"] {
            margin-left: auto !important;
            flex-shrink: 0 !important;
          }
          
          /* Ensure the container has proper flex behavior */
          .core-metrics-grid .metric-card .mb-3.d-flex {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
          }
          
          /* Override any display block styles on the column */
          .core-metrics-grid .col-12.d-flex {
            display: block !important;
          }
        }
        
        @media (max-width: 576px) {
          .core-metrics-grid .col-12 {
            margin-bottom: 12px;
          }
        }
        
        @media (min-width: 992px) and (max-width: 1199px) {
          .core-metrics-grid .col-lg-4 {
            flex: 0 0 33.333333%;
            max-width: 33.333333%;
          }
        }
        
        @media (min-width: 1200px) {
          .core-metrics-grid .col-xl-2 {
            flex: 0 0 16.666667%;
            max-width: 16.666667%;
          }
        }
      `}</style>
    </div>
  );
};

export default CoreMetricsGrid;